import React, { useState, useEffect } from "react";
import { db } from "../../services/firebase";
import { sendQuickAddFollowUp } from "../../services/emailService";
import {
	collection,
	onSnapshot,
	doc,
	updateDoc,
	deleteDoc,
	addDoc,
	query,
	orderBy,
} from "firebase/firestore";
import {
	ChevronDown,
	ChevronUp,
	MessageSquare,
	Trash2,
	Phone,
	Mail,
	Clock,
	Download,
	ArrowRight,
	GitPullRequest,
	UserPlus,
} from "lucide-react";
import Pipeline from "./Pipeline";

const STATUS_OPTIONS = ["new", "contacted", "converted", "closed"];

const STATUS_COLORS = {
	new: "bg-warning text-dark",
	contacted: "bg-info",
	converted: "bg-success",
	closed: "bg-secondary",
};

const Enquiries = ({ loading: parentLoading, onMoveToUsers }) => {
	const [enquiries, setEnquiries] = useState([]);
	const [loading, setLoading] = useState(true);
	const [expandedEnquiries, setExpandedEnquiries] = useState({});
	const [filterStatus, setFilterStatus] = useState("all");
	const [activeSubTab, setActiveSubTab] = useState("enquiries");
	const [quickAddForm, setQuickAddForm] = useState({
		name: "",
		email: "",
		phone: "",
		gdprConsent: false,
	});
	const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
	const [quickAddSuccess, setQuickAddSuccess] = useState("");
	const [quickAddError, setQuickAddError] = useState("");
	const [quickAddValidation, setQuickAddValidation] = useState({});

	useEffect(() => {
		const q = query(collection(db, "enquiries"), orderBy("createdAt", "desc"));
		const unsubscribe = onSnapshot(q, (snapshot) => {
			const data = snapshot.docs.map((d) => ({
				id: d.id,
				...d.data(),
			}));
			setEnquiries(data);
			setLoading(false);
		});
		return () => unsubscribe();
	}, []);

	const toggleEnquiry = (id) => {
		setExpandedEnquiries((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const updateStatus = async (id, newStatus) => {
		try {
			await updateDoc(doc(db, "enquiries", id), { status: newStatus });
		} catch (error) {
			console.error("Error updating enquiry status:", error);
		}
	};

	const deleteEnquiry = async (id) => {
		if (!window.confirm("Delete this enquiry?")) return;
		try {
			await deleteDoc(doc(db, "enquiries", id));
		} catch (error) {
			console.error("Error deleting enquiry:", error);
		}
	};

	const moveToPipeline = async (enquiry) => {
		if (
			!window.confirm(
				`Move ${enquiry.firstName} ${enquiry.lastName} to the pipeline?`,
			)
		)
			return;
		try {
			await addDoc(collection(db, "pipeline"), {
				clientName: `${enquiry.firstName} ${enquiry.lastName}`,
				sessionPlan: `${enquiry.sessionsPerWeek || "TBD"} sessions/week`,
				startDate: "",
				cardDetails: false,
				healthDetails: false,
				welcomeEmail: false,
				addedToGroupChat: false,
				followUp24h: false,
				followUp2Weeks: false,
				followUp1Month: false,
				cancelled: false,
				notes:
					[
						enquiry.motivation && `Motivation: ${enquiry.motivation}`,
						enquiry.readiness &&
							`Readiness: ${enquiry.readiness === "ready" ? "Ready to invest" : "Wants to learn more"}`,
						enquiry.callTime && `Best call time: ${enquiry.callTime}`,
						enquiry.source === "quick-add" && "Source: Quick Add",
					]
						.filter(Boolean)
						.join(". ") || "No additional notes",
				email: enquiry.email,
				phone: enquiry.phone,
				sourceEnquiryId: enquiry.id,
				createdAt: new Date().toISOString(),
			});
			// Update enquiry status to converted
			await updateDoc(doc(db, "enquiries", enquiry.id), {
				status: "converted",
			});
		} catch (error) {
			console.error("Error moving to pipeline:", error);
		}
	};

	const exportEnquiriesCSV = () => {
		const escapeCSV = (val) => {
			const str = String(val ?? "");
			return str.includes(",") || str.includes('"') || str.includes("\n")
				? `"${str.replace(/"/g, '""')}"`
				: str;
		};
		const headers = [
			"Date",
			"First Name",
			"Last Name",
			"Email",
			"Phone",
			"Sessions/Week",
			"Motivation",
			"Readiness",
			"Best Call Time",
			"Status",
		];
		const rows = filteredEnquiries.map((e) => [
			new Date(e.createdAt).toLocaleDateString(),
			e.firstName,
			e.lastName,
			e.email,
			e.phone,
			e.sessionsPerWeek,
			e.motivation || "",
			e.readiness === "ready" ? "Ready to invest" : "Wants to learn more",
			e.callTime,
			e.status,
		]);
		const csv = [headers, ...rows]
			.map((r) => r.map(escapeCSV).join(","))
			.join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const validateQuickAddForm = () => {
		const errors = {};
		if (!quickAddForm.name.trim()) errors.name = "Name is required";
		if (!quickAddForm.email.trim()) errors.email = "Email is required";
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(quickAddForm.email))
			errors.email = "Invalid email format";
		if (!quickAddForm.phone.trim()) errors.phone = "Phone is required";
		if (!quickAddForm.gdprConsent)
			errors.gdprConsent = "Consent is required to contact this person";
		return errors;
	};

	const handleQuickAddSubmit = async (e) => {
		e.preventDefault();
		const errors = validateQuickAddForm();
		setQuickAddValidation(errors);
		if (Object.keys(errors).length > 0) return;

		setQuickAddSubmitting(true);
		setQuickAddSuccess("");
		setQuickAddError("");

		try {
			// Split name into firstName/lastName
			const nameParts = quickAddForm.name.trim().split(" ");
			const firstName = nameParts[0];
			const lastName = nameParts.slice(1).join(" ") || "";

			// Save to Firestore
			await addDoc(collection(db, "enquiries"), {
				firstName,
				lastName,
				email: quickAddForm.email.trim(),
				phone: quickAddForm.phone.trim(),
				source: "quick-add",
				status: "new",
				createdAt: new Date().toISOString(),
			});

			// Send follow-up email
			const emailResult = await sendQuickAddFollowUp({
				contactName: quickAddForm.name.trim(),
				contactEmail: quickAddForm.email.trim(),
			});

			if (emailResult.success) {
				setQuickAddSuccess("Contact saved and follow-up email sent!");
			} else {
				setQuickAddSuccess("Contact saved successfully!");
				setQuickAddError(
					"Warning: Follow-up email could not be sent. Please follow up manually.",
				);
			}

			// Reset form
			setQuickAddForm({ name: "", email: "", phone: "", gdprConsent: false });
			setQuickAddValidation({});
		} catch (error) {
			console.error("Error saving quick-add contact:", error);
			setQuickAddError("Failed to save contact. Please try again.");
		} finally {
			setQuickAddSubmitting(false);
		}
	};

	const filteredEnquiries =
		filterStatus === "all"
			? enquiries
			: enquiries.filter((e) => e.status === filterStatus);

	const newCount = enquiries.filter((e) => e.status === "new").length;

	if (loading || parentLoading) {
		return (
			<div className="card">
				<div className="card-body text-center py-5">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="card">
			<div
				className="card-header"
				style={{ paddingBottom: 0, borderBottom: "none" }}
			>
				<h5 className="mb-3" style={{ fontSize: "1.1rem" }}>
					<MessageSquare className="me-2" size={20} />
					Leads & Pipeline
				</h5>
				{/* Tabs */}
				<ul
					className="nav nav-tabs"
					style={{
						flexWrap: "nowrap",
						borderBottom: "none",
						marginBottom: 0,
						gap: "0.5rem",
					}}
				>
					<li className="nav-item" style={{ flex: "1 1 0" }}>
						<button
							className={`nav-link ${activeSubTab === "enquiries" ? "active" : ""} w-100 text-center`}
							onClick={() => setActiveSubTab("enquiries")}
							style={
								activeSubTab === "enquiries"
									? {
											backgroundColor: "#d15a36",
											borderColor: "#d15a36",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
									: {
											backgroundColor: "rgba(209, 90, 54, 0.15)",
											borderColor: "transparent",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
							}
						>
							<MessageSquare size={14} className="me-1" />
							Enquiries
							{newCount > 0 && (
								<span
									className="badge bg-warning text-dark ms-1"
									style={{ fontSize: "0.65rem" }}
								>
									{newCount}
								</span>
							)}
						</button>
					</li>
					<li className="nav-item" style={{ flex: "1 1 0" }}>
						<button
							className={`nav-link ${activeSubTab === "pipeline" ? "active" : ""} w-100 text-center`}
							onClick={() => setActiveSubTab("pipeline")}
							style={
								activeSubTab === "pipeline"
									? {
											backgroundColor: "#d15a36",
											borderColor: "#d15a36",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
									: {
											backgroundColor: "rgba(209, 90, 54, 0.15)",
											borderColor: "transparent",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
							}
						>
							<GitPullRequest size={14} className="me-1" />
							Pipeline
						</button>
					</li>
					<li className="nav-item" style={{ flex: "1 1 0" }}>
						<button
							className={`nav-link ${activeSubTab === "quick-add" ? "active" : ""} w-100 text-center`}
							onClick={() => setActiveSubTab("quick-add")}
							style={
								activeSubTab === "quick-add"
									? {
											backgroundColor: "#d15a36",
											borderColor: "#d15a36",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
									: {
											backgroundColor: "rgba(209, 90, 54, 0.15)",
											borderColor: "transparent",
											color: "#fff",
											fontSize: "0.85rem",
											padding: "0.5rem 0.25rem",
											borderRadius: "0.375rem",
										}
							}
						>
							<UserPlus size={14} className="me-1" />
							Quick Add
						</button>
					</li>
				</ul>
			</div>

			<div className="card-body">
				{/* Enquiries Tab */}
				{activeSubTab === "enquiries" && (
					<>
						{/* Header Controls */}
						<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
							<div className="d-flex align-items-center gap-2">
								<span className="text-muted">
									{enquiries.length} total enquiries
								</span>
							</div>
							<div className="d-flex align-items-center gap-2">
								<select
									className="form-select form-select-sm"
									style={{ width: "auto" }}
									value={filterStatus}
									onChange={(e) => setFilterStatus(e.target.value)}
								>
									<option value="all">All Enquiries</option>
									{STATUS_OPTIONS.map((s) => (
										<option key={s} value={s}>
											{s.charAt(0).toUpperCase() + s.slice(1)}
										</option>
									))}
								</select>
								<button
									className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
									onClick={exportEnquiriesCSV}
									disabled={filteredEnquiries.length === 0}
									title="Export to CSV"
								>
									<Download size={16} />
									Export
								</button>
							</div>
						</div>

						{/* Summary */}
						<div className="d-flex gap-2 mb-3 flex-wrap">
							<div
								className="p-2 bg-light rounded text-center flex-fill"
								style={{ minWidth: 80 }}
							>
								<small
									className="text-muted d-block"
									style={{ fontSize: "0.7rem" }}
								>
									Total
								</small>
								<strong>{enquiries.length}</strong>
							</div>
							<div
								className="p-2 bg-light rounded text-center flex-fill"
								style={{ minWidth: 80 }}
							>
								<small
									className="text-muted d-block"
									style={{ fontSize: "0.7rem" }}
								>
									New
								</small>
								<strong>{newCount}</strong>
							</div>
							<div
								className="p-2 bg-light rounded text-center flex-fill"
								style={{ minWidth: 80 }}
							>
								<small
									className="text-muted d-block"
									style={{ fontSize: "0.7rem" }}
								>
									Converted
								</small>
								<strong>
									{enquiries.filter((e) => e.status === "converted").length}
								</strong>
							</div>
						</div>

						{filteredEnquiries.length === 0 ? (
							<div className="text-center py-5">
								<p className="text-muted">
									{filterStatus === "all"
										? "No enquiries yet."
										: `No ${filterStatus} enquiries.`}
								</p>
							</div>
						) : (
							<div>
								{filteredEnquiries.map((enquiry) => {
									const isExpanded = expandedEnquiries[enquiry.id];
									return (
										<div key={enquiry.id} className="notification-card mb-3">
											<div
												className="notification-header"
												onClick={() => toggleEnquiry(enquiry.id)}
												style={{ cursor: "pointer" }}
											>
												<div className="d-flex justify-content-between align-items-start gap-2">
													<div
														className="d-flex align-items-center gap-2 flex-grow-1"
														style={{ minWidth: 0 }}
													>
														<span
															className={`badge ${STATUS_COLORS[enquiry.status] || "bg-secondary"}`}
															style={{ fontSize: "0.7rem", flexShrink: 0 }}
														>
															{enquiry.status}
														</span>
														{enquiry.source === "quick-add" && (
															<span
																className="badge bg-light text-dark border"
																style={{ fontSize: "0.65rem", flexShrink: 0 }}
															>
																Quick Add
															</span>
														)}
														<h6
															className="mb-0"
															style={{
																fontSize: "0.9rem",
																overflow: "hidden",
																textOverflow: "ellipsis",
																whiteSpace: "nowrap",
															}}
														>
															{enquiry.firstName} {enquiry.lastName}
														</h6>
														{enquiry.sessionsPerWeek && (
															<small
																className="text-muted d-none d-md-inline"
																style={{ flexShrink: 0 }}
															>
																{enquiry.sessionsPerWeek} sessions/week
															</small>
														)}
													</div>
													<div className="d-flex align-items-center gap-1 flex-shrink-0">
														<small className="text-muted d-none d-sm-inline">
															{new Date(enquiry.createdAt).toLocaleDateString()}
														</small>
														{enquiry.status !== "converted" && (
															<button
																className="btn btn-sm btn-outline-success p-1"
																onClick={(e) => {
																	e.stopPropagation();
																	moveToPipeline(enquiry);
																}}
																title="Move to Pipeline"
																style={{ lineHeight: 1 }}
															>
																<ArrowRight size={14} />
															</button>
														)}
														<button
															className="btn btn-sm btn-outline-danger p-1"
															onClick={(e) => {
																e.stopPropagation();
																deleteEnquiry(enquiry.id);
															}}
															title="Delete enquiry"
															style={{ lineHeight: 1 }}
														>
															<Trash2 size={14} />
														</button>
														{isExpanded ? (
															<ChevronUp size={18} />
														) : (
															<ChevronDown size={18} />
														)}
													</div>
												</div>
											</div>

											{isExpanded && (
												<div className="notification-body mt-3">
													<div className="row g-2 mb-3">
														<div className="col-md-6">
															<div className="d-flex align-items-center gap-2">
																<Mail size={16} className="text-muted" />
																<span>{enquiry.email}</span>
															</div>
														</div>
														<div className="col-md-6">
															<div className="d-flex align-items-center gap-2">
																<Phone size={16} className="text-muted" />
																<span>{enquiry.phone}</span>
															</div>
														</div>
													</div>

													{enquiry.sessionsPerWeek && (
														<div className="mb-2">
															<small className="text-muted d-block">
																Sessions per week:
															</small>
															<span>{enquiry.sessionsPerWeek}</span>
														</div>
													)}

													{enquiry.motivation && (
														<div className="mb-2">
															<small className="text-muted d-block">
																Motivation:
															</small>
															<span>{enquiry.motivation}</span>
														</div>
													)}

													{enquiry.readiness && (
														<div className="mb-2">
															<small className="text-muted d-block">
																Ready to invest:
															</small>
															<span>
																{enquiry.readiness === "ready"
																	? "Yes, ready to take action"
																	: "Would like to learn more first"}
															</span>
														</div>
													)}

													{enquiry.callTime && (
														<div className="mb-3">
															<div className="d-flex align-items-center gap-2">
																<Clock size={16} className="text-muted" />
																<small className="text-muted">
																	Best call time:
																</small>
																<span>{enquiry.callTime}</span>
															</div>
														</div>
													)}

													<div className="d-flex align-items-center gap-3 flex-wrap">
														<div className="d-flex align-items-center gap-2">
															<label className="form-label mb-0 me-1">
																<small>Status:</small>
															</label>
															<select
																className="form-select form-select-sm"
																style={{ width: "auto" }}
																value={enquiry.status}
																onChange={(e) =>
																	updateStatus(enquiry.id, e.target.value)
																}
															>
																{STATUS_OPTIONS.map((s) => (
																	<option key={s} value={s}>
																		{s.charAt(0).toUpperCase() + s.slice(1)}
																	</option>
																))}
															</select>
														</div>
														{enquiry.status !== "converted" && (
															<button
																className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
																onClick={() => moveToPipeline(enquiry)}
																title="Move to Pipeline"
															>
																<ArrowRight size={14} />
																Move to Pipeline
															</button>
														)}
													</div>
												</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</>
				)}

				{/* Pipeline Tab */}
				{activeSubTab === "pipeline" && (
					<Pipeline loading={parentLoading} onMoveToUsers={onMoveToUsers} />
				)}

				{/* Quick Add Tab */}
				{activeSubTab === "quick-add" && (
					<div>
						<h6 className="mb-3" style={{ fontSize: "0.95rem" }}>
							<UserPlus size={18} className="me-2" />
							Quick Add Contact
						</h6>
						<p className="text-muted small mb-3">
							Quickly capture a new contact's details. A follow-up email will be
							sent automatically.
						</p>

						{quickAddSuccess && (
							<div className="alert alert-success small py-2">
								{quickAddSuccess}
							</div>
						)}
						{quickAddError && (
							<div className="alert alert-danger small py-2">
								{quickAddError}
							</div>
						)}

						<form onSubmit={handleQuickAddSubmit}>
							<div className="mb-3">
								<label className="form-label small">Name</label>
								<input
									type="text"
									className={`form-control ${quickAddValidation.name ? "is-invalid" : ""}`}
									placeholder="First and Last Name"
									value={quickAddForm.name}
									onChange={(e) =>
										setQuickAddForm((prev) => ({
											...prev,
											name: e.target.value,
										}))
									}
									disabled={quickAddSubmitting}
								/>
								{quickAddValidation.name && (
									<div className="invalid-feedback">
										{quickAddValidation.name}
									</div>
								)}
							</div>
							<div className="mb-3">
								<label className="form-label small">Email</label>
								<input
									type="email"
									className={`form-control ${quickAddValidation.email ? "is-invalid" : ""}`}
									placeholder="email@example.com"
									value={quickAddForm.email}
									onChange={(e) =>
										setQuickAddForm((prev) => ({
											...prev,
											email: e.target.value,
										}))
									}
									disabled={quickAddSubmitting}
								/>
								{quickAddValidation.email && (
									<div className="invalid-feedback">
										{quickAddValidation.email}
									</div>
								)}
							</div>
							<div className="mb-3">
								<label className="form-label small">Phone</label>
								<input
									type="tel"
									className={`form-control ${quickAddValidation.phone ? "is-invalid" : ""}`}
									placeholder="07xxx xxxxxx"
									value={quickAddForm.phone}
									onChange={(e) =>
										setQuickAddForm((prev) => ({
											...prev,
											phone: e.target.value,
										}))
									}
									disabled={quickAddSubmitting}
								/>
								{quickAddValidation.phone && (
									<div className="invalid-feedback">
										{quickAddValidation.phone}
									</div>
								)}
							</div>
							<div className="mb-3">
								<div className="form-check">
									<input
										type="checkbox"
										className={`form-check-input ${quickAddValidation.gdprConsent ? "is-invalid" : ""}`}
										id="gdprConsent"
										checked={quickAddForm.gdprConsent}
										onChange={(e) =>
											setQuickAddForm((prev) => ({
												...prev,
												gdprConsent: e.target.checked,
											}))
										}
										disabled={quickAddSubmitting}
									/>
									<label
										className="form-check-label small"
										htmlFor="gdprConsent"
									>
										This person has given consent for Urban 59 to contact them
									</label>
									{quickAddValidation.gdprConsent && (
										<div className="invalid-feedback">
											{quickAddValidation.gdprConsent}
										</div>
									)}
								</div>
							</div>
							<button
								type="submit"
								className="btn w-100"
								style={{ backgroundColor: "#d15a36", color: "#fff" }}
								disabled={quickAddSubmitting}
							>
								{quickAddSubmitting
									? "Saving..."
									: "Save Contact & Send Follow-Up"}
							</button>
						</form>
					</div>
				)}
			</div>
		</div>
	);
};

export default Enquiries;
